import { useEffect, useMemo, useState } from "react";
import type { ChartData, ChartOptions } from "chart.js";
import { Scatter } from "react-chartjs-2";
import { getAxisCoordinates } from "../services/api";
import type { AxisChartRootArgs } from "../types";
import ChartFullscreenWrapper from "./ChartFullscreenWrapper";
import Loader from "./Loader";

type AxisCoordinate = {
  x: number;
  y: number;
  z: number;
  chainage: number;
  metadata?: Record<string, unknown>;
};

export default function AxisCharts({ axisId }: AxisChartRootArgs) {
  const [processing, setProcessing] = useState(true);
  const [coordinates, setCoordinates] = useState<AxisCoordinate[]>([]);

  useEffect(() => {
    let mounted = true;
    setProcessing(true);

    void getAxisCoordinates(axisId)
      .then((result) => {
        if (!mounted) {
          return;
        }
        setCoordinates((result ?? []) as AxisCoordinate[]);
      })
      .finally(() => {
        if (mounted) {
          setProcessing(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [axisId]);

  const ordered = useMemo(
    () => [...coordinates].sort((a, b) => (a.chainage < b.chainage ? -1 : a.chainage > b.chainage ? 1 : 0)),
    [coordinates]
  );

  const xyData = useMemo(
    () => ({
      datasets: [
        {
          label: "XY Dataset",
          data: ordered.map((item) => ({ x: item.x, y: item.y, metadata: item.metadata })),
          borderColor: "rgba(81, 86, 209, 1)",
          pointBackgroundColor: "rgba(81, 86, 209, .7)",
          pointHoverBackgroundColor: "rgba(81, 86, 209, 1)",
          pointBorderColor: "rgba(81, 86, 209, .7)",
          pointHoverBorderColor: "rgba(81, 86, 209, 1)",
          borderWidth: 3,
          pointRadius: 7,
          pointHoverRadius: 10,
          pointBorderWidth: 2,
          fill: false,
          tension: 0,
          showLine: true,
          parsing: false
        }
      ]
    }),
    [ordered]
  );

  const zData = useMemo(
    () => ({
      datasets: [
        {
          label: "Z Dataset",
          data: ordered.map((item) => ({ x: item.chainage, y: item.z, metadata: item.metadata })),
          borderWidth: 3,
          borderColor: "rgba(242, 96, 96, 1)",
          pointBackgroundColor: "rgba(242, 96, 96, .7)",
          pointHoverBackgroundColor: "rgba(242, 96, 96, 1)",
          pointBorderColor: "rgba(242, 96, 96, .7)",
          pointRadius: 7,
          pointHoverRadius: 10,
          pointBorderWidth: 2,
          backgroundColor: "rgba(232, 69, 69, .15)",
          fill: true,
          tension: 0,
          showLine: true,
          parsing: false
        }
      ]
    }),
    [ordered]
  );

  const commonTooltip = {
    callbacks: {
      label: (context: { dataset: { data: Array<{ metadata?: Record<string, unknown> }> }; dataIndex: number }) => {
        const point = context.dataset.data[context.dataIndex] ?? {};
        return (point.metadata?.tooltipText as string | undefined) ?? "Profile";
      }
    }
  };

  const xyOptions = useMemo(() => {
    const xVals = ordered.map((item) => item.x);
    const yVals = ordered.map((item) => item.y);

    return {
      plugins: {
        legend: { display: false },
        tooltip: commonTooltip
      },
      scales: {
        "x-axis-1": {
          type: "linear",
          min: xVals.length > 0 ? Math.min(...xVals) - 1 : -1,
          max: xVals.length > 0 ? Math.max(...xVals) + 1 : 1,
          title: { display: true, text: "Coordinate X" },
          grid: { color: "rgba(137, 139, 186, .3)", display: true }
        },
        "y-axis-1": {
          type: "linear",
          min: yVals.length > 0 ? Math.min(...yVals) - 1 : -1,
          max: yVals.length > 0 ? Math.max(...yVals) + 1 : 1,
          title: { display: true, text: "Coordinate Y" },
          grid: { color: "rgba(137, 139, 186, .3)", display: true }
        }
      }
    };
  }, [ordered]);

  const zOptions = useMemo(() => {
    const chainage = ordered.map((item) => item.chainage);
    const zVals = ordered.map((item) => item.z);

    return {
      plugins: {
        legend: { display: false },
        tooltip: commonTooltip
      },
      scales: {
        "x-axis-1": {
          type: "linear",
          min: chainage.length > 0 ? Math.min(...chainage) - 1 : -1,
          max: chainage.length > 0 ? Math.max(...chainage) + 1 : 1,
          title: { display: true, text: "Chainage" },
          grid: { color: "rgba(137, 139, 186, .3)", display: true }
        },
        "y-axis-1": {
          type: "linear",
          min: zVals.length > 0 ? Math.min(...zVals) - 1 : -1,
          max: zVals.length > 0 ? Math.max(...zVals) + 1 : 1,
          title: { display: true, text: "Coordinate Z" },
          grid: { color: "rgba(137, 139, 186, .3)", display: true }
        }
      }
    };
  }, [ordered]);

  if (processing) {
    return <Loader />;
  }

  return (
    <div>
      <label className="chartTitle">Chart Planimetry</label>
      <ChartFullscreenWrapper>
        <Scatter
          data={xyData as unknown as ChartData<"scatter">}
          options={xyOptions as unknown as ChartOptions<"scatter">}
        />
      </ChartFullscreenWrapper>

      <label className="chartTitle">Chart Vertical Alignment</label>
      <ChartFullscreenWrapper>
        <Scatter
          data={zData as unknown as ChartData<"scatter">}
          options={zOptions as unknown as ChartOptions<"scatter">}
        />
      </ChartFullscreenWrapper>
    </div>
  );
}
